import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Flame,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trophy,
  RotateCcw,
  Sparkles,
  Target,
  Upload,
  File,
  Image,
  X,
  AlertTriangle,
  Share2,
  Copy,
  ExternalLink,
  Check,
  Play,
  ArrowRight,
  Zap,
  BarChart3,
  LogIn,
  LogOut,
  User,
  FlaskConical,
  ListChecks,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import usePro from "@/hooks/usePro";
import ProGateModal from "@/components/ProGateModal";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Check if we're in a preview/dev environment (NOT production).
 * Uses env var first, then hostname fallback.
 * This is a frontend guard - backend also blocks the endpoint in production.
 */
const getIsPreviewEnv = () => {
  // Check env var first (set at build time)
  const appEnv = process.env.REACT_APP_APP_ENV;
  if (appEnv) {
    return appEnv.toLowerCase() !== "production";
  }
  
  // Hostname fallback - check if running on preview infrastructure
  const host = window.location.hostname || "";
  return host.includes("preview") || host.includes("emergentagent.com") || host === "localhost";
};

const isPreviewEnv = getIsPreviewEnv();

// Subject options
const SUBJECTS = [
  "Math",
  "Science",
  "English Language Arts (ELA)",
  "Reading",
  "Social Studies",
  "World Languages",
  "CTE / Career & Life Skills",
];

// World Languages options
const WORLD_LANGUAGES = [
  "Spanish",
  "French",
];

// Grade level options
const GRADE_LEVELS = [
  "3rd Grade",
  "4th Grade",
  "5th Grade",
  "6th Grade",
  "7th Grade",
  "8th Grade",
  "9th Grade",
  "10th Grade",
  "11th Grade",
  "12th Grade",
];

// Streak target options
const STREAK_TARGETS = [
  { value: 5, label: "5 in a row" },
  { value: 10, label: "10 in a row" },
  { value: 15, label: "15 in a row" },
];

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

export default function StreakQuiz() {
  // Pro/Paywall state
  const { isPro, paywallEnabled, requiresPro, getProToken, unlockPro } = usePro();
  const [showProGateModal, setShowProGateModal] = useState(false);
  const [proGateFeature, setProGateFeature] = useState("");

  // Setup state
  const [subject, setSubject] = useState("");
  const [gradeLevel, setGradeLevel] = useState("");
  const [streakTarget, setStreakTarget] = useState(10);
  const [studyGuideText, setStudyGuideText] = useState("");
  const [advancedMathBeta, setAdvancedMathBeta] = useState(false);
  const [worldLanguage, setWorldLanguage] = useState(""); // For World Languages subject
  const [includeInteractive, setIncludeInteractive] = useState(false); // TEI toggle
  
  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Quiz state
  const [gameState, setGameState] = useState("setup");
  const [loadingMessage, setLoadingMessage] = useState("");
  const [questionPool, setQuestionPool] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Keep Going mode state
  const [bonusStreak, setBonusStreak] = useState(0);
  const [showBonusEndMessage, setShowBonusEndMessage] = useState(false);
  const [bonusEndedAt, setBonusEndedAt] = useState(0);

  // Share challenge state
  const [isCreatingChallenge, setIsCreatingChallenge] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [effectiveGradeLevel, setEffectiveGradeLevel] = useState("");
  const [shareLink, setShareLink] = useState("");
  const [copiedLink, setCopiedLink] = useState(false);
  const [generatedChallengeId, setGeneratedChallengeId] = useState("");
  
  // MODEL B: Creator auth state (replaces email-based dashboard)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [creatorToken, setCreatorToken] = useState(null);
  const [creatorProfile, setCreatorProfile] = useState(null);
  const [dashboardLink, setDashboardLink] = useState("");
  const [copiedDashboardLink, setCopiedDashboardLink] = useState(false);
  
  // Login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [devMagicLink, setDevMagicLink] = useState("");
  const [devLoginAvailable, setDevLoginAvailable] = useState(false);
  const [devLoggingIn, setDevLoggingIn] = useState(false);

  // Stats for success screen
  const [stats, setStats] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    longestStreak: 0,
  });

  const [combinedStudyText, setCombinedStudyText] = useState("");
  const navigate = useNavigate();
  
  // Check if we're in preview/dev mode
  const isPreviewMode = window.location.hostname.includes('preview') || 
                        window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1';
  
  // Check for existing creator token on mount
  React.useEffect(() => {
    const savedProfile = localStorage.getItem("creator_profile");

    // Show cached (non-sensitive) profile instantly, then verify via cookie
    if (savedProfile) {
      try {
        const profile = JSON.parse(savedProfile);
        setCreatorProfile(profile);
        setIsLoggedIn(true);
      } catch (e) {
        console.error("[StreakQuiz] Failed to parse cached profile:", e);
      }
    }

    // Verify session using the httpOnly creator_token cookie
    axios.get(`${API}/auth/me`).then(res => {
      if (res.data.success) {
        setIsLoggedIn(true);
        setCreatorProfile(res.data.creator);
        localStorage.setItem("creator_profile", JSON.stringify(res.data.creator));
      } else {
        localStorage.removeItem("creator_profile");
        setIsLoggedIn(false);
        setCreatorProfile(null);
      }
    }).catch(() => {
      localStorage.removeItem("creator_profile");
      setIsLoggedIn(false);
      setCreatorProfile(null);
    });
    
    // Check for magic link verification in URL
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get("token");
    if (magicToken) {
      verifyMagicLink(magicToken);
    }
    
    // Check if login modal should be opened
    if (params.get("login") === "1") {
      setShowLoginModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Check if dev login is available (preview environment only)
  useEffect(() => {
    const checkDevLogin = async () => {
      try {
        const res = await axios.get(`${API}/auth/dev-login-status`);
        setDevLoginAvailable(res.data.available === true);
      } catch {
        setDevLoginAvailable(false);
      }
    };
    checkDevLogin();
  }, []);
  
  // Handle dev login for testing
  const handleDevLogin = async (creatorKey = "creator_a") => {
    setDevLoggingIn(true);
    try {
      const res = await axios.post(`${API}/auth/dev-login?creator_key=${creatorKey}`);
      if (res.data.success) {
        localStorage.setItem("creator_profile", JSON.stringify(res.data.creator));
        setIsLoggedIn(true);
        setCreatorProfile(res.data.creator);
        setShowLoginModal(false);
        toast.success(`Dev login: ${res.data.creator.name}`);
        navigate("/my-challenges");
      }
    } catch (err) {
      console.error("Dev login error:", err);
      toast.error(err.response?.data?.detail || "Dev login failed");
    } finally {
      setDevLoggingIn(false);
    }
  };
  
  // Claim pending challenge after successful login
  const claimPendingChallenge = async (jwt) => {
    const pendingChallengeId = localStorage.getItem("pending_claim_challenge_id");
    if (!pendingChallengeId) return null;
    
    try {
      const res = await axios.post(`${API}/creators/me/claim-challenge`, {
        challenge_id: pendingChallengeId
      });
      
      if (res.data.success) {
        localStorage.removeItem("pending_claim_challenge_id");
        toast.success("Challenge claimed! Dashboard now available.");
        return {
          dashboard_token: res.data.dashboard_token,
          dashboard_url: res.data.dashboard_url
        };
      }
    } catch (error) {
      console.error("Failed to claim challenge:", error);
      // Don't show error - challenge may have been owned by someone else
    }
    return null;
  };
  
  const verifyMagicLink = async (token) => {
    try {
      console.log("[StreakQuiz] Verifying magic link token...");
      const res = await axios.get(`${API}/auth/verify-magic-link?token=${token}`);
      if (res.data.success) {
        console.log("[StreakQuiz] Magic link verified, storing auth...");
        localStorage.setItem("creator_profile", JSON.stringify(res.data.creator));
        setIsLoggedIn(true);
        setCreatorProfile(res.data.creator);
        toast.success("Signed in successfully!");
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Claim any pending challenge
        const claimed = await claimPendingChallenge();
        if (claimed) {
          setDashboardLink(`${window.location.origin}${claimed.dashboard_url}`);
        }
      }
    } catch (error) {
      console.error("[StreakQuiz] Magic link verification failed:", error);
      toast.error("Invalid or expired sign-in link");
    }
  };
  
  const handleRequestMagicLink = async () => {
    if (!loginEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/request-magic-link`, {
        email: loginEmail.trim()
      });
      
      if (res.data.success) {
        setMagicLinkSent(true);
        
        // Preview mode: show instant verify if dev_token returned
        if (res.data.dev_token) {
          setDevMagicLink(res.data.dev_magic_link);
        } else {
          toast.success("Sign-in link sent to your email!");
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send sign-in link");
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleDevVerify = async () => {
    if (devMagicLink) {
      const token = devMagicLink.split("token=")[1];
      await verifyMagicLink(token);
      setShowLoginModal(false);
      setMagicLinkSent(false);
      setDevMagicLink("");
      setLoginEmail("");
    }
  };
  
  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    localStorage.removeItem("creator_profile");
    localStorage.removeItem("pending_claim_challenge_id");
    setCreatorToken(null);
    setIsLoggedIn(false);
    setCreatorProfile(null);
    setDashboardLink("");
    toast.success("Signed out");
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const ext = file.name.toLowerCase().split(".").pop();
      const isValidType = ["pdf", "png", "jpg", "jpeg"].includes(ext);
      const isValidSize = file.size <= 10 * 1024 * 1024;

      if (!isValidType) {
        errors.push(`${file.name}: Invalid file type. Only PDF, PNG, JPG allowed.`);
      } else if (!isValidSize) {
        errors.push(`${file.name}: File too large. Maximum 10MB allowed.`);
      } else {
        const isDuplicate = uploadedFiles.some((f) => f.name === file.name && f.size === file.size);
        if (!isDuplicate) {
          validFiles.push(file);
        }
      }
    });

    if (errors.length > 0) setFileErrors((prev) => [...prev, ...errors]);
    if (validFiles.length > 0) setUploadedFiles((prev) => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearFileErrors = () => setFileErrors([]);

  const extractTextFromFiles = async () => {
    const extractedTexts = [];
    const newErrors = [];
    const proToken = getProToken();
    const headers = {
      "Content-Type": "multipart/form-data",
      ...(proToken ? { Authorization: `Bearer ${proToken}` } : {})
    };

    for (const file of uploadedFiles) {
      const ext = file.name.toLowerCase().split(".").pop();
      
      try {
        const formData = new FormData();
        formData.append("file", file);

        if (ext === "pdf") {
          setLoadingMessage(`Extracting text from ${file.name}...`);
          const response = await axios.post(`${API}/extract-pdf-text`, formData, { headers });
          if (response.data.success && response.data.text) {
            extractedTexts.push(`\n--- From ${file.name} ---\n${response.data.text}`);
          } else {
            newErrors.push(`Could not read text from ${file.name}`);
          }
        } else if (["png", "jpg", "jpeg"].includes(ext)) {
          setLoadingMessage(`Reading text from image ${file.name}...`);
          const response = await axios.post(`${API}/extract-image-text`, formData, { headers });
          if (response.data.success && response.data.text) {
            extractedTexts.push(`\n--- From ${file.name} ---\n${response.data.text}`);
          } else {
            newErrors.push(`Could not read text from ${file.name}`);
          }
        }
      } catch (error) {
        console.error(`Error extracting from ${file.name}:`, error);
        // Check if it's a Pro required error
        if (error.response?.status === 402) {
          setShowProGateModal(true);
          setProGateFeature("Study Guide Upload");
          return { extractedTexts: [], errors: ["Pro required for file uploads"] };
        }
        newErrors.push(`Error processing ${file.name}`);
      }
    }

    return { extractedTexts, errors: newErrors };
  };

  const generateQuestions = async (studyText, count = 30) => {
    const response = await axios.post(`${API}/generate-questions`, {
      subject,
      grade_level: gradeLevel,
      study_guide_text: studyText || null,
      question_count: count,
    });
    return response.data.questions;
  };

  const handleStartChallenge = async () => {
    setGameState("loading");
    setLoadingMessage("Preparing your challenge...");
    setFileErrors([]);

    try {
      let combinedText = studyGuideText.trim();

      if (uploadedFiles.length > 0) {
        const { extractedTexts, errors } = await extractTextFromFiles();
        if (errors.length > 0) setFileErrors(errors);
        if (extractedTexts.length > 0) {
          combinedText = combinedText + "\n" + extractedTexts.join("\n");
        }
      }

      setCombinedStudyText(combinedText);
      setLoadingMessage("Generating questions...");
      const questions = await generateQuestions(combinedText, 30);

      if (questions.length === 0) {
        toast.error("Failed to generate questions. Please try again.");
        setGameState("setup");
        return;
      }

      setQuestionPool(questions);
      setCurrentQuestionIndex(0);
      setCurrentStreak(0);
      setTotalAnswered(0);
      setSelectedAnswer(null);
      setShowResult(false);
      setStats({ totalQuestions: 0, correctAnswers: 0, longestStreak: 0 });
      setGameState("playing");
      toast.success(`Generated ${questions.length} questions. Let's go!`);
    } catch (error) {
      console.error("Error starting challenge:", error);
      toast.error(error.response?.data?.detail || "Failed to start challenge");
      setGameState("setup");
    }
  };

  const handleShareChallenge = async () => {
    // Check if Pro is required for challenge creation
    if (requiresPro("challengeCreation")) {
      setProGateFeature("Challenge Creation");
      setShowProGateModal(true);
      return;
    }

    setIsCreatingChallenge(true);
    setLoadingMessage("Creating shareable challenge...");

    try {
      let combinedText = studyGuideText.trim();

      if (uploadedFiles.length > 0) {
        setLoadingMessage("Processing uploaded files...");
        const { extractedTexts, errors } = await extractTextFromFiles();
        if (errors.length > 0) setFileErrors(errors);
        // Check if Pro gate was triggered during file extraction
        if (errors.includes("Pro required for file uploads")) {
          setIsCreatingChallenge(false);
          setLoadingMessage("");
          return;
        }
        if (extractedTexts.length > 0) {
          combinedText = combinedText + "\n" + extractedTexts.join("\n");
        }
      }

      setLoadingMessage("Generating questions for your class...");
      
      // MODEL B: Use creator token if logged in, otherwise Pro token
      const authToken = creatorToken || getProToken();
      
      // Helper to check if subject is Math (for backend compatibility)
      const isMathSubject = subject === "Math" || subject === "Mathematics";
      
      const response = await axios.post(`${API}/challenges`, {
        subject,
        grade_level: gradeLevel,
        streak_target: streakTarget,
        study_guide_text: combinedText || null,
        advanced_math_beta: isMathSubject ? advancedMathBeta : false,
        // World Languages specific field
        world_language: subject === "World Languages" ? (worldLanguage || "Spanish") : null,
        // TEI: Include interactive questions
        include_interactive: includeInteractive,
      }, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });

      if (response.data.success) {
        const challengeUrl = `${window.location.origin}/challenge/${response.data.challenge_id}`;
        setShareLink(challengeUrl);
        setGeneratedChallengeId(response.data.challenge_id);
        
        // MODEL B: Dashboard link only available for logged-in creators
        if (response.data.dashboard_token) {
          setDashboardLink(`${window.location.origin}/dashboard/${response.data.dashboard_token}`);
        } else {
          setDashboardLink("");
          // Store challenge ID for claiming after login
          if (!isLoggedIn) {
            localStorage.setItem("pending_claim_challenge_id", response.data.challenge_id);
          }
        }
        
        setShowShareDialog(true);
        toast.success(`Challenge created with ${response.data.question_count} questions!`);

        // Reflect the effective (possibly capped) grade level in the dialog
        setEffectiveGradeLevel(response.data.grade_level ? `${response.data.grade_level} Grade` : gradeLevel);

        // Grade-level cap warning from backend
        if (response.data.grade_level_warning) {
          toast.warning(response.data.grade_level_warning, { duration: 6000 });
        }
      }
    } catch (error) {
      console.error("Error creating challenge:", error);
      // Check if it's a Pro required error
      if (error.response?.status === 402) {
        setProGateFeature("Challenge Creation");
        setShowProGateModal(true);
      } else {
        toast.error(error.response?.data?.detail || "Failed to create challenge");
      }
    } finally {
      setIsCreatingChallenge(false);
      setLoadingMessage("");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopiedLink(true);
      toast.success("Link copied!");
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleCopyDashboardLink = async () => {
    try {
      await navigator.clipboard.writeText(dashboardLink);
      setCopiedDashboardLink(true);
      toast.success("Dashboard link copied!");
      setTimeout(() => setCopiedDashboardLink(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleStartWithExistingQuestions = async () => {
    if (!generatedChallengeId) {
      toast.error("No challenge found. Please create one first.");
      return;
    }

    setShowShareDialog(false);
    setGameState("loading");
    setLoadingMessage("Loading your challenge...");

    try {
      const response = await axios.get(`${API}/challenges/${generatedChallengeId}`);
      if (response.data.success && response.data.challenge) {
        const challenge = response.data.challenge;
        // Use questions in exact stored order - no shuffling for consistency
        setQuestionPool([...challenge.question_pool]);
        setCurrentQuestionIndex(0);
        setCurrentStreak(0);
        setTotalAnswered(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setStats({ totalQuestions: 0, correctAnswers: 0, longestStreak: 0 });
        setGameState("playing");
        toast.success("Let's go!");
      } else {
        toast.error("Failed to load challenge");
        setGameState("setup");
      }
    } catch (error) {
      console.error("Error loading challenge:", error);
      toast.error("Failed to load challenge");
      setGameState("setup");
    }
  };

  const loadMoreQuestions = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const moreQuestions = await generateQuestions(combinedStudyText, 20);
      setQuestionPool((prev) => [...prev, ...moreQuestions]);
      toast.success(`Added ${moreQuestions.length} more questions!`);
    } catch (error) {
      console.error("Error loading more questions:", error);
      toast.error("Failed to load more questions");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const currentQuestion = questionPool[currentQuestionIndex];
  
  // DEFENSIVE: Validate current question has required fields
  const isValidQuestion = (q) => {
    if (!q || typeof q !== 'object') return false;
    if (!q.question_text) return false;
    // Multiple choice requires options array
    if (!q.question_type || q.question_type === 'multiple_choice') {
      if (!Array.isArray(q.options) || q.options.length === 0) return false;
      if (typeof q.correct_option !== 'number') return false;
    }
    return true;
  };
  
  // DEFENSIVE: Skip invalid questions automatically
  const getValidCurrentQuestion = () => {
    if (isValidQuestion(currentQuestion)) return currentQuestion;
    
    // Find next valid question
    for (let i = currentQuestionIndex + 1; i < questionPool.length; i++) {
      if (isValidQuestion(questionPool[i])) {
        setCurrentQuestionIndex(i);
        return questionPool[i];
      }
    }
    return null;
  };
  
  const validQuestion = getValidCurrentQuestion();

  const handleSelectAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    if (!validQuestion) return; // DEFENSIVE: Don't submit if question is invalid

    const isCorrect = selectedAnswer === validQuestion.correct_option;
    setShowResult(true);
    setTotalAnswered((prev) => prev + 1);

    // Handle Keep Going mode
    if (gameState === "keepgoing") {
      if (isCorrect) {
        const newBonusStreak = bonusStreak + 1;
        setBonusStreak(newBonusStreak);
        toast.success(`Correct! Bonus streak: ${newBonusStreak}`, { duration: 1500 });
      } else {
        setBonusEndedAt(bonusStreak);
        setShowBonusEndMessage(true);
        setBonusStreak(0);
      }
      return;
    }

    // Normal challenge mode
    setStats((prev) => ({
      totalQuestions: prev.totalQuestions + 1,
      correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0),
      longestStreak: Math.max(prev.longestStreak, isCorrect ? currentStreak + 1 : currentStreak),
    }));

    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      toast.success(`Correct! Streak: ${newStreak}/${streakTarget}`, { duration: 1500 });

      if (newStreak >= streakTarget) {
        setTimeout(() => {
          setStats((prev) => ({ ...prev, longestStreak: newStreak }));
          setGameState("success");
        }, 1500);
      }
    } else {
      setCurrentStreak(0);
      toast.error("Incorrect - Streak reset!", { duration: 2000 });
    }
  };

  const handleNextQuestion = async () => {
    // Clear bonus end message when moving to next question
    setShowBonusEndMessage(false);
    
    const nextIndex = currentQuestionIndex + 1;

    if (nextIndex >= questionPool.length - 3) {
      loadMoreQuestions();
    }

    if (nextIndex < questionPool.length) {
      setCurrentQuestionIndex(nextIndex);
      setSelectedAnswer(null);
      setShowResult(false);
    } else if (isLoadingMore) {
      toast.info("Loading more questions...");
    }
  };

  const handleRestart = () => {
    setGameState("setup");
    setQuestionPool([]);
    setCurrentQuestionIndex(0);
    setCurrentStreak(0);
    setTotalAnswered(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setUploadedFiles([]);
    setFileErrors([]);
    setCombinedStudyText("");
    setBonusStreak(0);
    setShowBonusEndMessage(false);
  };

  const handleKeepGoing = () => {
    // Continue with questions in Keep Going mode
    // Don't reset main streak or stats - challenge remains completed
    setBonusStreak(0);
    setShowBonusEndMessage(false);
    setSelectedAnswer(null);
    setShowResult(false);
    // Continue from where we left off (next question after completion)
    const nextIndex = (currentQuestionIndex + 1) % questionPool.length;
    setCurrentQuestionIndex(nextIndex);
    setGameState("keepgoing");
  };

  const handlePlayAgain = () => {
    if (questionPool.length > 0) {
      // Restart from beginning in same order - no shuffling for consistency
      setCurrentQuestionIndex(0);
      setCurrentStreak(0);
      setTotalAnswered(0);
      setSelectedAnswer(null);
      setShowResult(false);
      setBonusStreak(0);
      setShowBonusEndMessage(false);
      setStats({ totalQuestions: 0, correctAnswers: 0, longestStreak: 0 });
      setGameState("playing");
    } else {
      handleStartChallenge();
    }
  };

  // ==================== RENDER ====================

  // Setup Screen
  if (gameState === "setup") {
    return (
      <div className="min-h-screen bg-paper py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Top Header with Creator Login */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-ember/10 rounded-xl">
                <Flame className="w-6 h-6 text-ember" />
              </div>
              <span className="font-heading text-xl text-sage-900">StreakChallenge</span>
            </div>
            
            {/* Creator Login / Account Section */}
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate("/my-challenges")}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="my-challenges-btn"
                  aria-label="View my challenges"
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" />
                  My Challenges
                </Button>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  size="sm"
                  className="rounded-full text-muted-foreground hover:text-sage-900"
                  data-testid="header-logout-btn"
                  aria-label="Log out of your account"
                >
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Log out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-end gap-1">
                <Button
                  onClick={() => setShowLoginModal(true)}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  data-testid="creator-login-btn"
                  title="Log in to save challenges and access dashboards later"
                  aria-label="Creator login - log in to save challenges and access dashboards"
                >
                  <User className="w-4 h-4 mr-1.5" />
                  Creator Login
                </Button>
                <span className="font-body text-[10px] text-muted-foreground/70 hidden sm:inline max-w-[180px] text-right leading-tight">
                  Create challenges, access your dashboard, and track student progress.
                </span>
              </div>
            )}
          </div>
          
          {/* Setup Card */}
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-sage-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-sage-500" />
                Set Up Your Challenge
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Subject */}
              <div>
                <Label className="font-body">Subject</Label>
                <Select value={subject} onValueChange={(val) => {
                  setSubject(val);
                  // Reset world language when changing subjects
                  if (val !== "World Languages") {
                    setWorldLanguage("");
                  }
                }}>
                  <SelectTrigger className="mt-2" data-testid="subject-select">
                    <SelectValue placeholder="Choose your subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="font-body text-xs text-muted-foreground mt-1.5">
                  This helps tailor the questions to the right content area.
                </p>
              </div>

              {/* World Language Selector - Only shown when World Languages is selected */}
              {subject === "World Languages" && (
                <div>
                  <Label className="font-body">Language</Label>
                  <Select value={worldLanguage} onValueChange={setWorldLanguage}>
                    <SelectTrigger className="mt-2" data-testid="world-language-select">
                      <SelectValue placeholder="Choose a language" />
                    </SelectTrigger>
                    <SelectContent>
                      {WORLD_LANGUAGES.map((lang) => (
                        <SelectItem key={lang} value={lang}>{lang}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="font-body text-xs text-muted-foreground mt-1.5">
                    Questions will focus on vocabulary and basic grammar in this language.
                  </p>
                </div>
              )}

              {/* Grade Level */}
              <div>
                <Label className="font-body">Grade Level</Label>
                <Select value={gradeLevel} onValueChange={setGradeLevel}>
                  <SelectTrigger className="mt-2" data-testid="grade-select">
                    <SelectValue placeholder="Choose your grade level" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADE_LEVELS.map((g) => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="font-body text-xs text-muted-foreground mt-1.5">
                  Questions will be written at this grade's difficulty level.
                </p>
              </div>

              {/* Streak Goal */}
              <div>
                <Label className="font-body">Streak Goal</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {STREAK_TARGETS.map((target) => (
                    <Button
                      key={target.value}
                      variant={streakTarget === target.value ? "default" : "outline"}
                      onClick={() => setStreakTarget(target.value)}
                      className={`rounded-xl py-6 ${
                        streakTarget === target.value ? "bg-ember hover:bg-ember/90 text-white" : ""
                      }`}
                      data-testid={`streak-target-${target.value}`}
                    >
                      <Flame className="w-4 h-4 mr-2" />
                      {target.label}
                    </Button>
                  ))}
                </div>
                <p className="font-body text-xs text-muted-foreground mt-1.5">
                  Shorter streaks build confidence; longer streaks challenge mastery.
                </p>
              </div>

              {/* Study Guide */}
              <div>
                <Label className="font-body">
                  Study Guide (Optional)
                  <span className="text-muted-foreground font-normal ml-2">
                    Paste notes or study material
                  </span>
                </Label>
                <Textarea
                  value={studyGuideText}
                  onChange={(e) => setStudyGuideText(e.target.value)}
                  placeholder="Paste your study guide, notes, or vocabulary list here..."
                  className="mt-2 min-h-[120px]"
                  data-testid="study-guide-textarea"
                />
                {studyGuideText.length > 0 && (
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    {studyGuideText.length} characters
                  </p>
                )}
                {(subject === "Mathematics" || subject === "Math") && (
                  <p className="font-body text-xs text-amber-600 mt-1.5">
                    Math note: Works best for vocabulary, word problems, and conceptual questions. Symbol-heavy algebra (exponents/radicals) and open-ended expressions may not translate accurately yet.
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <Label className="font-body">
                  Upload Study Materials (Optional)
                  <span className="text-muted-foreground font-normal ml-2">PDF, PNG, JPG</span>
                </Label>
                
                <div
                  className="mt-2 border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-sage-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="file-upload-area"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="font-body text-sm text-muted-foreground">
                    Click to upload or drag and drop
                  </p>
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    PDF, PNG, JPG (max 10MB each)
                  </p>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {uploadedFiles.map((file, index) => {
                      const isPdf = file.name.toLowerCase().endsWith(".pdf");
                      return (
                        <div key={index} className="flex items-center justify-between p-3 bg-secondary rounded-xl">
                          <div className="flex items-center gap-3">
                            {isPdf ? <File className="w-5 h-5 text-red-500" /> : <Image className="w-5 h-5 text-blue-500" />}
                            <div>
                              <p className="font-body text-sm text-sage-900 truncate max-w-[200px]">{file.name}</p>
                              <p className="font-body text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                            </div>
                          </div>
                          <Button variant="ghost" size="icon" onClick={() => removeFile(index)} className="text-muted-foreground hover:text-red-500">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {fileErrors.length > 0 && (
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-grow">
                        {fileErrors.map((error, index) => (
                          <p key={index} className="font-body text-sm text-amber-700">{error}</p>
                        ))}
                      </div>
                      <Button variant="ghost" size="icon" onClick={clearFileErrors} className="text-amber-500 hover:text-amber-700 -mr-2 -mt-1">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Source Summary */}
              <div className="p-3 bg-muted rounded-xl">
                <p className="font-body text-sm text-muted-foreground">
                  {!subject || !gradeLevel ? (
                    <>Select a subject and grade level to continue</>
                  ) : studyGuideText.trim() || uploadedFiles.length > 0 ? (
                    <>
                      Questions will be generated from:{" "}
                      <span className="text-sage-900">
                        {[
                          studyGuideText.trim() && "Study Guide text",
                          uploadedFiles.length > 0 && `${uploadedFiles.length} uploaded file${uploadedFiles.length > 1 ? "s" : ""}`,
                        ].filter(Boolean).join(" + ")}
                      </span>
                    </>
                  ) : (
                    <>
                      Questions will be generated for <span className="text-sage-900">{subject}</span> at{" "}
                      <span className="text-sage-900">{gradeLevel}</span> level
                    </>
                  )}
                </p>
              </div>

              {/* Experimental Math Mode Toggle - Only for Math subjects */}
              {(subject === "Mathematics" || subject === "Math") && (
                <div className="p-4 bg-amber-50/50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-amber-100 rounded-lg mt-0.5">
                      <FlaskConical className="w-4 h-4 text-amber-600" />
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="advanced-math-toggle" className="font-body font-medium text-sage-900 cursor-pointer">
                          Protect streaks on complex math (optional)
                        </Label>
                        <Switch
                          id="advanced-math-toggle"
                          checked={advancedMathBeta}
                          onCheckedChange={setAdvancedMathBeta}
                          data-testid="advanced-math-beta-toggle"
                        />
                      </div>
                      <p className="font-body text-xs text-muted-foreground mt-1.5">
                        Enable this for algebra, radicals, multi-step equations. If the system is uncertain, your student's streak stays protected.
                      </p>
                      <p className="font-body text-xs text-sage-600 mt-1">
                        Basic math (patterns, addition, multiplication) is always graded normally — no protection needed.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* TEI Toggle - Include Interactive Questions */}
              <div className="p-4 bg-purple-50/50 border border-purple-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg mt-0.5">
                    <ListChecks className="w-4 h-4 text-purple-600" />
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="interactive-toggle" className="font-body font-medium text-sage-900 cursor-pointer">
                        Include interactive questions (optional)
                      </Label>
                      <Switch
                        id="interactive-toggle"
                        checked={includeInteractive}
                        onCheckedChange={setIncludeInteractive}
                        data-testid="include-interactive-toggle"
                      />
                    </div>
                    <p className="font-body text-xs text-muted-foreground mt-1.5">
                      Add variety with multi-select, fill-in-the-blank, and matching questions.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Button - Single Create Button */}
              <Button
                onClick={handleShareChallenge}
                disabled={!subject || !gradeLevel || isCreatingChallenge}
                className="w-full rounded-full py-6 text-lg font-medium btn-press bg-sage-900 hover:bg-sage-900/90 text-white"
                data-testid="create-challenge-btn"
              >
                {isCreatingChallenge ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Challenge...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Create Streak Challenge
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="mt-6 text-center">
            <p className="font-body text-sm text-muted-foreground">
              Reach your streak goal by answering {streakTarget} in a row.
              <br />
              A wrong answer resets your streak to 0 — keep building momentum.
            </p>
          </div>
        </div>

        {/* Share Dialog - Challenge Ready Modal */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-sage-900 flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                Challenge Ready
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-5 mt-4">
              {/* Share This Challenge Section */}
              <div>
                <p className="font-body text-sm font-medium text-sage-900 mb-3">Share with your students</p>
                <Button
                  onClick={handleCopyLink}
                  className="w-full rounded-full bg-sage-900 hover:bg-sage-900/90"
                  data-testid="copy-challenge-link-btn"
                >
                  {copiedLink ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-green-400" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Challenge Link
                    </>
                  )}
                </Button>
                <p className="font-body text-xs text-muted-foreground mt-2 text-center">
                  Anyone with this link can take the challenge.
                </p>
              </div>

              {/* Divider */}
              {(dashboardLink || !isLoggedIn) && (
                <div className="border-t border-border" />
              )}

              {/* Challenge Dashboard Section (logged-in creators only) */}
              {dashboardLink && isLoggedIn && (
                <div data-testid="dashboard-link-section">
                  <p className="font-body text-sm font-medium text-sage-900 mb-3">Your Dashboard</p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => window.open(dashboardLink, "_blank")}
                      variant="outline"
                      className="flex-1 rounded-full"
                      data-testid="open-dashboard-btn"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Open Dashboard
                    </Button>
                    <Button
                      onClick={handleCopyDashboardLink}
                      variant="outline"
                      className="rounded-full px-4"
                      data-testid="copy-dashboard-link-btn"
                    >
                      {copiedDashboardLink ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="font-body text-xs text-muted-foreground mt-2 text-center">
                    Track student attempts, accuracy, and progress.
                  </p>
                </div>
              )}
              
              {/* Login prompt for anonymous creators */}
              {!isLoggedIn && !dashboardLink && (
                <div className="p-4 bg-sage-50 border border-sage-200 rounded-xl" data-testid="login-prompt-section">
                  <p className="font-body text-sm text-sage-700 mb-3">
                    Sign in to track student progress and access your dashboard.
                  </p>
                  <Button
                    onClick={() => setShowLoginModal(true)}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-sage-300 text-sage-700 hover:bg-sage-100"
                  >
                    <User className="w-4 h-4 mr-1.5" />
                    Sign in for Dashboard
                  </Button>
                </div>
              )}

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={() => window.open(shareLink, "_blank")}
                  variant="outline"
                  className="flex-1 rounded-full"
                  data-testid="preview-challenge-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Challenge
                </Button>
                <Button
                  onClick={() => {
                    setShowShareDialog(false);
                    handleStartWithExistingQuestions();
                  }}
                  variant="outline"
                  className="flex-1 rounded-full"
                  data-testid="start-now-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Now
                </Button>
              </div>

              {/* Challenge Info Footer */}
              <div className="p-3 bg-muted rounded-xl">
                <p className="font-body text-xs text-muted-foreground text-center">
                  <span className="font-medium text-sage-900">{subject}</span> • {effectiveGradeLevel || gradeLevel} • {streakTarget} in a row
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Pro Gate Modal */}
        <ProGateModal
          isOpen={showProGateModal}
          onClose={() => setShowProGateModal(false)}
          featureName={proGateFeature}
          onUnlock={unlockPro}
        />
        
        {/* Login Modal (Model B) */}
        <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-sage-900">
                {magicLinkSent ? "Check Your Email" : "Creator Sign In"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {!magicLinkSent ? (
                <>
                  {/* Dev Login Section - Preview Only (double-gated) */}
                  {isPreviewEnv && devLoginAvailable && (
                    <div className="p-4 bg-violet-50 border border-violet-200 rounded-xl" data-testid="login-dev-section">
                      <div className="flex items-center gap-2 mb-3">
                        <FlaskConical className="w-4 h-4 text-violet-600" />
                        <p className="font-body text-sm font-medium text-violet-800">
                          Dev Login (Preview Only)
                        </p>
                      </div>
                      <p className="font-body text-xs text-violet-600 mb-3">
                        Use test creator accounts for Creator Edit Mode testing
                      </p>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleDevLogin("creator_a")}
                          variant="outline"
                          size="sm"
                          disabled={devLoggingIn}
                          className="flex-1 rounded-full border-violet-300 text-violet-700 hover:bg-violet-100"
                          data-testid="login-dev-a-btn"
                        >
                          {devLoggingIn ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Creator A"
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDevLogin("creator_b")}
                          variant="outline"
                          size="sm"
                          disabled={devLoggingIn}
                          className="flex-1 rounded-full border-violet-300 text-violet-700 hover:bg-violet-100"
                          data-testid="login-dev-b-btn"
                        >
                          {devLoggingIn ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            "Creator B"
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {isPreviewEnv && devLoginAvailable && (
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <div className="flex-1 h-px bg-border"></div>
                      <span>or use email</span>
                      <div className="flex-1 h-px bg-border"></div>
                    </div>
                  )}
                  
                  <p className="font-body text-sm text-muted-foreground">
                    Enter your email to receive a sign-in link. No password needed.
                  </p>
                  <div>
                    <Label className="font-body">Email</Label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="you@school.edu"
                      className="mt-2"
                      data-testid="login-email-input"
                      onKeyDown={(e) => e.key === "Enter" && handleRequestMagicLink()}
                    />
                  </div>
                  <Button
                    onClick={handleRequestMagicLink}
                    disabled={loginLoading || !loginEmail.trim()}
                    className="w-full rounded-full bg-sage-900 hover:bg-sage-900/90"
                    data-testid="send-magic-link-btn"
                  >
                    {loginLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Sign-In Link"
                    )}
                  </Button>
                </>
              ) : (
                <>
                  {/* Different copy for production vs preview */}
                  {isPreviewMode && devMagicLink ? (
                    <>
                      <p className="font-body text-sm text-muted-foreground">
                        Ready to sign in as <span className="font-medium text-sage-900">{loginEmail}</span>.
                      </p>
                      
                      {/* Preview-only instant sign-in */}
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="font-body text-xs text-amber-700 mb-3">
                          Email delivery is disabled in preview. Use the button below to sign in instantly.
                        </p>
                        <Button
                          onClick={handleDevVerify}
                          className="w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                          data-testid="preview-signin-btn"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Sign In (Preview)
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="font-body text-sm text-muted-foreground">
                        We sent a sign-in link to <span className="font-medium text-sage-900">{loginEmail}</span>.
                        Click the link in your email to continue.
                      </p>
                      <p className="font-body text-xs text-muted-foreground">
                        Didn't receive it? Check your spam folder or try again in a few minutes.
                      </p>
                    </>
                  )}
                  
                  <Button
                    onClick={() => {
                      setMagicLinkSent(false);
                      setDevMagicLink("");
                    }}
                    variant="outline"
                    className="w-full rounded-full"
                  >
                    Use Different Email
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Loading Screen
  if (gameState === "loading") {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-ember mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">
              {loadingMessage || "Preparing your challenge..."}
            </h2>
            <p className="font-body text-muted-foreground">This may take a moment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success Screen
  if (gameState === "success") {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4">
        <Card className="max-w-md w-full rounded-2xl shadow-lg overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-float">
              <Trophy className="w-12 h-12 text-amber-500" />
            </div>

            <div className="bg-gradient-to-br from-ember to-amber-500 text-white rounded-2xl p-6 mb-6">
              <h2 className="font-heading text-3xl mb-2">Streak Master</h2>
              <p className="font-body text-xl opacity-90">{streakTarget} in a row!</p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-secondary rounded-xl">
                <p className="font-heading text-2xl text-sage-900">{stats.totalQuestions}</p>
                <p className="font-body text-sm text-muted-foreground">Questions</p>
              </div>
              <div className="p-4 bg-secondary rounded-xl">
                <p className="font-heading text-2xl text-sage-900">
                  {stats.totalQuestions > 0 ? Math.round((stats.correctAnswers / stats.totalQuestions) * 100) : 0}%
                </p>
                <p className="font-body text-sm text-muted-foreground">Accuracy</p>
              </div>
            </div>

            <p className="font-body text-muted-foreground mb-6">{subject} • {gradeLevel}</p>

            <div className="space-y-3">
              <div>
                <Button onClick={handleKeepGoing} className="w-full rounded-full py-6 font-medium bg-ember hover:bg-ember/90 text-white">
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Keep Going
                </Button>
                <p className="font-body text-xs text-muted-foreground text-center mt-2">
                  Practice a little more and see how long your bonus streak lasts.
                </p>
              </div>
              <Button onClick={handleRestart} variant="outline" className="w-full rounded-full py-6 font-medium">
                New Challenge
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Keep Going Screen (playing in bonus mode)
  if (gameState === "keepgoing" && validQuestion) {
    // DEFENSIVE: Ensure options array exists
    const options = Array.isArray(validQuestion.options) ? validQuestion.options : [];
    const correctOption = typeof validQuestion.correct_option === 'number' ? validQuestion.correct_option : -1;
    
    return (
      <div className="min-h-screen bg-paper" data-testid="keepgoing-screen">
        <header className="border-b border-border bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-sm text-muted-foreground">{subject} • {gradeLevel}</span>
              <Button variant="ghost" size="sm" onClick={handleRestart} className="text-muted-foreground hover:text-sage-900">
                Quit
              </Button>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${bonusStreak > 0 ? "bg-purple-100" : "bg-muted"}`}>
                  <Zap className={`w-6 h-6 ${bonusStreak > 0 ? "text-purple-500" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <p className="font-heading text-3xl text-sage-900">
                    {bonusStreak}
                  </p>
                  <p className="font-body text-xs text-purple-600">Bonus Streak</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-body text-sm text-green-600 flex items-center justify-end gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Challenge Complete!
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  Keep going for bonus points
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-body text-lg sm:text-xl text-sage-900 mb-6 leading-relaxed">
                {validQuestion.question_text || "Loading question..."}
              </h2>

              <div className="space-y-3 mb-6">
                {options.length > 0 ? options.map((option, index) => {
                  let optionClass = "quiz-option";
                  let icon = null;

                  if (showResult) {
                    if (index === correctOption) {
                      optionClass += " correct";
                      icon = <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
                    } else if (index === selectedAnswer && index !== correctOption) {
                      optionClass += " incorrect";
                      icon = <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
                    }
                  } else if (selectedAnswer === index) {
                    optionClass += " selected";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleSelectAnswer(index)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl border-2 border-border text-left font-body flex items-center gap-3 ${optionClass}`}
                    >
                      <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-secondary-foreground flex-shrink-0">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="flex-grow">{option}</span>
                      {icon}
                    </button>
                  );
                }) : (
                  <p className="text-muted-foreground text-center py-4">Loading options...</p>
                )}
              </div>

              {showResult && selectedAnswer !== correctOption && validQuestion.explanation && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-medium text-amber-800 mb-1">Explanation</p>
                      <p className="font-body text-amber-700">{validQuestion.explanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {showBonusEndMessage && (
                <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <p className="font-body text-purple-700">
                      Bonus streak ended at {bonusEndedAt}. Keep going!
                    </p>
                  </div>
                </div>
              )}

              {!showResult ? (
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswer === null}
                  className="w-full rounded-full py-6 font-medium btn-press bg-sage-900 hover:bg-sage-900/90 text-white"
                >
                  Submit Answer
                </Button>
              ) : (
                <Button
                  onClick={handleNextQuestion}
                  className="w-full rounded-full py-6 font-medium btn-press bg-purple-500 hover:bg-purple-600 text-white"
                >
                  Continue
                </Button>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Playing Screen
  if (gameState === "playing" && validQuestion) {
    const progress = (currentStreak / streakTarget) * 100;
    // DEFENSIVE: Ensure options array exists
    const options = Array.isArray(validQuestion.options) ? validQuestion.options : [];
    const correctOption = typeof validQuestion.correct_option === 'number' ? validQuestion.correct_option : -1;

    return (
      <div className="min-h-screen bg-paper" data-testid="quiz-screen">
        <header className="border-b border-border bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-sm text-muted-foreground">{subject} • {gradeLevel}</span>
              <Button variant="ghost" size="sm" onClick={handleRestart} className="text-muted-foreground hover:text-sage-900">
                Quit
              </Button>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${currentStreak > 0 ? "bg-ember/10" : "bg-muted"}`}>
                  <Flame className={`w-6 h-6 ${currentStreak > 0 ? "text-ember" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <p className="font-heading text-3xl text-sage-900">
                    {currentStreak}<span className="text-lg text-muted-foreground">/{streakTarget}</span>
                  </p>
                  <p className="font-body text-xs text-muted-foreground">Current Streak</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-body text-sm text-muted-foreground">Target: {streakTarget} in a row</p>
                <p className="font-body text-xs text-muted-foreground">
                  Q{totalAnswered + 1} • Pool: {questionPool.length - currentQuestionIndex} left
                </p>
              </div>
            </div>

            <Progress value={progress} className="h-3" />
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-body text-lg sm:text-xl text-sage-900 mb-6 leading-relaxed">
                {validQuestion.question_text || "Loading question..."}
              </h2>

              <div className="space-y-3 mb-6">
                {options.length > 0 ? options.map((option, index) => {
                  let optionClass = "quiz-option";
                  let icon = null;

                  if (showResult) {
                    if (index === correctOption) {
                      optionClass += " correct";
                      icon = <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
                    } else if (index === selectedAnswer && index !== correctOption) {
                      optionClass += " incorrect";
                      icon = <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
                    }
                  } else if (selectedAnswer === index) {
                    optionClass += " selected";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleSelectAnswer(index)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl border-2 border-border text-left font-body flex items-center gap-3 ${optionClass}`}
                    >
                      <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-secondary-foreground flex-shrink-0">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <span className="flex-grow">{option}</span>
                      {icon}
                    </button>
                  );
                }) : (
                  <p className="text-muted-foreground text-center py-4">Loading options...</p>
                )}
              </div>

              {showResult && selectedAnswer !== correctOption && validQuestion.explanation && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-medium text-amber-800 mb-1">Explanation</p>
                      <p className="font-body text-amber-700">{validQuestion.explanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {showResult && selectedAnswer !== correctOption && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <p className="font-body text-red-700">Streak reset to 0. Keep going - you can do it!</p>
                  </div>
                </div>
              )}

              {showResult && selectedAnswer === correctOption && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="font-body text-green-700">Correct! Streak: {currentStreak}/{streakTarget}</p>
                  </div>
                </div>
              )}

              {!showResult ? (
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={selectedAnswer === null}
                  className="w-full rounded-full py-6 font-medium btn-press bg-sage-900 hover:bg-sage-900/90 text-white"
                >
                  Submit Answer
                </Button>
              ) : currentStreak < streakTarget && (
                <Button
                  onClick={handleNextQuestion}
                  disabled={isLoadingMore && currentQuestionIndex >= questionPool.length - 1}
                  className="w-full rounded-full py-6 font-medium btn-press bg-ember hover:bg-ember/90 text-white"
                >
                  {isLoadingMore && currentQuestionIndex >= questionPool.length - 1 ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Loading...</>
                  ) : "Continue"}
                </Button>
              )}
            </CardContent>
          </Card>
        </main>

        {/* Pro Gate Modal */}
        <ProGateModal
          isOpen={showProGateModal}
          onClose={() => setShowProGateModal(false)}
          featureName={proGateFeature}
          onUnlock={unlockPro}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <Button onClick={handleRestart} className="rounded-full">Start Over</Button>
      
      {/* Pro Gate Modal */}
      <ProGateModal
        isOpen={showProGateModal}
        onClose={() => setShowProGateModal(false)}
        featureName={proGateFeature}
        onUnlock={unlockPro}
      />
    </div>
  );
}
